import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Users, Package, ShoppingCart, Target } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface UserStats {
  id: string;
  name: string;
  email: string;
  lines_used: number;
  accounts_created: number;
  avg_orders: number;
  weekly_accounts: number;
  weekly_quota: number;
  quota_reduction: number;
  week_start: string;
  week_end: string;
}

interface Account {
  id: string;
  email: string;
  created_at: string;
  status: string;
  created_by: string;
  orders: string;
}

const StatisticsTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [userStats, setUserStats] = useState<UserStats[]>([]);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const getWeekBoundaries = () => {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 is Sunday, 1 is Monday, etc.
    
    // Calculate days to subtract to get to Monday (if Sunday, go back 6 days)
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    
    // Create Monday date (start of week)
    const monday = new Date(now);
    monday.setDate(now.getDate() - daysToMonday);
    monday.setHours(0, 0, 0, 0);
    
    // Create Sunday date (end of week)
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    
    return { weekStart: monday, weekEnd: sunday };
  };

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      console.log('🔍 Starting statistics fetch...');

      // Get all users
      const { data: users, error: usersError } = await supabase
        .from('users')
        .select('*')
        .order('email');

      if (usersError) throw usersError;
      console.log(`🧑‍💼 Found ${users.length} users`);

      // Get line usage statistics from system_logs
      const { data: lineStats, error: lineError } = await supabase
        .from('system_logs')
        .select('user_id, users(id, name, email)')
        .eq('action', 'GET_LINE');

      if (lineError) throw lineError;
      console.log(`📝 Found ${lineStats?.length || 0} line usage logs`);

      // Get week boundaries
      const { weekStart, weekEnd } = getWeekBoundaries();
      console.log(`📅 Week boundaries: ${weekStart.toISOString()} to ${weekEnd.toISOString()}`);

      // Get weekly quota
      const { data: quotaData, error: quotaError } = await supabase
        .from('account_quotas')
        .select('weekly_quota')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (quotaError && !quotaError.message.includes('No rows found')) throw quotaError;
      const baseQuota = quotaData?.weekly_quota || 5;
      console.log(`🎯 Base weekly quota: ${baseQuota}`);

      // Get all accounts for total counts and average orders
      const { data: allAccounts, error: allAccountsError } = await supabase
        .from('accounts')
        .select('created_by, orders');

      if (allAccountsError) throw allAccountsError;
      console.log(`📊 Found ${allAccounts?.length || 0} total accounts`);

      // Get RP-based quota reductions for all users
      const { data: rewardsData, error: rewardsError } = await supabase
        .from('user_rewards')
        .select('user_id, quota_reduction');

      if (rewardsError && !rewardsError.message.includes('No rows found')) throw rewardsError;
      console.log(`🎁 Found ${rewardsData?.length || 0} user reward records`);

      // Create a map of user_id to quota_reduction
      const reductionsMap = new Map(
        rewardsData?.map(reward => [reward.user_id, reward.quota_reduction]) || []
      );

      // Process statistics
      const statsMap = new Map<string, UserStats>();

      // Initialize stats for all users
      users.forEach(user => {
        const quotaReduction = reductionsMap.get(user.id) || 0;
        statsMap.set(user.id, {
          id: user.id,
          name: user.name || user.email,
          email: user.email,
          lines_used: 0,
          accounts_created: 0,
          avg_orders: 0,
          weekly_accounts: 0,
          weekly_quota: Math.max(0, baseQuota - quotaReduction), // Allow quota to be reduced to 0
          quota_reduction: quotaReduction,
          week_start: weekStart.toISOString(),
          week_end: weekEnd.toISOString()
        });
      });

      // Process line usage
      lineStats?.forEach((log) => {
        const user = log.users;
        if (!user) return;

        const stats = statsMap.get(user.id);
        if (stats) {
          stats.lines_used++;
        }
      });

      // Process all accounts for total counts and average orders
      allAccounts?.forEach((account) => {
        const stats = statsMap.get(account.created_by);
        if (stats) {
          stats.accounts_created++;
          stats.avg_orders = (stats.avg_orders * (stats.accounts_created - 1) + 
            (parseInt(account.orders) || 0)) / stats.accounts_created;
        }
      });

      // Get weekly accounts for each user
      console.log('🔢 Starting weekly account counting for each user...');
      
      // Fetch all accounts for the current week
      const { data: weeklyAccounts, error: weeklyAccountsError } = await supabase
        .from('accounts')
        .select('id, email, created_at, status, created_by, orders')
        .gte('created_at', weekStart.toISOString())
        .lte('created_at', weekEnd.toISOString());
        
      if (weeklyAccountsError) {
        console.error('❌ Error fetching weekly accounts:', weeklyAccountsError);
        throw weeklyAccountsError;
      }
      
      console.log(`📊 Found ${weeklyAccounts?.length || 0} total accounts for this week`);
      
      // Group accounts by user
      const accountsByUser = new Map<string, Account[]>();
      weeklyAccounts?.forEach(account => {
        if (!accountsByUser.has(account.created_by)) {
          accountsByUser.set(account.created_by, []);
        }
        accountsByUser.get(account.created_by)?.push(account);
      });
      
      // Process weekly accounts for each user
      for (const user of users) {
        try {
          console.log(`👤 Counting accounts for user: ${user.email} (${user.id})`);
          
          const userAccounts = accountsByUser.get(user.id) || [];
          console.log(`✅ User ${user.email} has ${userAccounts.length} accounts this week`);
          
          // Log each account's details for debugging
          if (userAccounts.length > 0) {
            console.log(`📋 Actual accounts for ${user.email}:`, userAccounts);
            userAccounts.forEach((account, index) => {
              console.log(`   Account ${index + 1}: ID=${account.id}, Email=${account.email}, Created=${account.created_at}`);
            });
          }
          
          const stats = statsMap.get(user.id);
          if (stats) {
            stats.weekly_accounts = userAccounts.length;
          }
        } catch (error) {
          console.error(`❌ Error processing user ${user.id}:`, error);
          // Continue with other users even if one fails
        }
      }

      // Convert to array and sort by most active users
      const sortedStats = Array.from(statsMap.values())
        .sort((a, b) => (b.lines_used + b.accounts_created) - (a.lines_used + a.accounts_created));

      console.log('📊 Final statistics:', sortedStats);
      setUserStats(sortedStats);
    } catch (error) {
      console.error('❌ Error fetching statistics:', error);
      toast.error('Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-6">User Statistics</h2>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <div className="flex items-center">
                  <Package className="h-4 w-4 mr-1" />
                  Lines Used
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <div className="flex items-center">
                  <Users className="h-4 w-4 mr-1" />
                  Total Accounts
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <div className="flex items-center">
                  <Target className="h-4 w-4 mr-1" />
                  Weekly Progress
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <div className="flex items-center">
                  <ShoppingCart className="h-4 w-4 mr-1" />
                  Avg. Orders
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <div className="flex items-center">
                  <BarChart className="h-4 w-4 mr-1" />
                  Activity Score
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {userStats.map((stat) => {
              const progressPercentage = stat.weekly_quota > 0 
                ? (stat.weekly_accounts / stat.weekly_quota) * 100 
                : stat.weekly_accounts > 0 ? 100 : 0;

              return (
                <tr key={stat.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <img
                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(stat.name)}&background=random`}
                        alt={stat.name}
                        className="h-8 w-8 rounded-full"
                      />
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {stat.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {stat.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {stat.lines_used}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {stat.accounts_created}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {stat.weekly_accounts} {stat.weekly_quota > 0 ? `of ${stat.weekly_quota}` : '(No Quota)'}
                          {stat.quota_reduction > 0 && (
                            <span className="ml-1 text-xs text-indigo-600 dark:text-indigo-400">
                              (-{stat.quota_reduction} RP)
                            </span>
                          )}
                        </span>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {Math.round(progressPercentage)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                        <div
                          className={`h-2.5 rounded-full ${
                            progressPercentage >= 100
                              ? 'bg-green-600 dark:bg-green-500'
                              : 'bg-indigo-600 dark:bg-indigo-500'
                          }`}
                          style={{ width: `${Math.min(100, progressPercentage)}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {stat.avg_orders.toFixed(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-indigo-600 dark:bg-indigo-400"
                          style={{ 
                            width: `${Math.min(100, ((stat.lines_used + stat.accounts_created) / 
                              (Math.max(...userStats.map(s => s.lines_used + s.accounts_created)) || 1)) * 100)}%` 
                          }}
                        />
                      </div>
                      <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                        {stat.lines_used + stat.accounts_created}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StatisticsTab;